import React, { useEffect, useRef } from 'react';
import {
  Calendar,
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  RefreshCw,
  Table2,
} from 'lucide-react';
import { ICON_STROKE_WIDTH } from '../../constants';
import { CRITICAL_RESULTS_ACK_STATUS_OPTIONS } from '../../constants/criticalResultsDashboard.constants';
import type { CriticalResultsExportFormat } from '../../types/criticalResultsDashboard.types';

export interface CriticalResultsFilterOption {
  value: string;
  label: string;
}

interface CriticalResultsFilterBarProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  facilityOptions: CriticalResultsFilterOption[];
  facilityId: number | null;
  onFacilityChange: (value: number) => void;
  showFacilityFilter: boolean;
  department: string;
  departmentOptions: CriticalResultsFilterOption[];
  onDepartmentChange: (value: string) => void;
  criticalityBand: string;
  onCriticalityBandChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  escalationLevel: string;
  onEscalationLevelChange: (value: string) => void;
  referringClinician: string;
  referringClinicianOptions: CriticalResultsFilterOption[];
  onReferringClinicianChange: (value: string) => void;
  autoRefresh: boolean;
  onAutoRefreshChange: (value: boolean) => void;
  lastRefreshedLabel: string;
  refreshIntervalSec?: number;
  loading: boolean;
  refreshing?: boolean;
  exporting?: boolean;
  exportMenuOpen?: boolean;
  onExportMenuToggle?: () => void;
  onExport?: (format: CriticalResultsExportFormat) => void;
  onClear: () => void;
  onRefresh: () => void;
}

function formatFilterDate(isoDate: string): string {
  if (!isoDate) {
    return '';
  }
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

function FilterDate({
  label,
  value,
  onChange,
  min,
  max,
}: Readonly<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
}>) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <div className="crd-filter-field">
      <span className="crd-filter-label">{label}</span>
      <label
        className="crd-filter-control crd-filter-control--date"
        htmlFor={`crd-date-${label}`}
      >
        <span className="crd-filter-value">{formatFilterDate(value)}</span>
        <Calendar
          className="crd-filter-icon"
          size={16}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
        <input
          ref={inputRef}
          id={`crd-date-${label}`}
          type="date"
          className="crd-filter-date-input"
          value={value}
          min={min}
          max={max}
          onChange={event => onChange(event.target.value)}
        />
      </label>
    </div>
  );
}

function formatAutoRefreshLabel(autoRefresh: boolean, refreshIntervalSec: number): string {
  if (!autoRefresh) {
    return 'Auto-refresh';
  }
  const usesMinuteInterval = refreshIntervalSec >= 60 && refreshIntervalSec % 60 === 0;
  const intervalText = usesMinuteInterval
    ? `${refreshIntervalSec / 60} min`
    : `${refreshIntervalSec} sec`;
  return `Auto-refresh every ${intervalText}`;
}

type ExportMenuOption = {
  format: CriticalResultsExportFormat;
  label: string;
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
};

function CriticalResultsExportMenu({
  exportWrapRef,
  exporting,
  loading,
  exportMenuOpen,
  onExportMenuToggle,
  onExport,
  exportOptions,
}: Readonly<{
  exportWrapRef: React.RefObject<HTMLDivElement | null>;
  exporting: boolean;
  loading: boolean;
  exportMenuOpen: boolean;
  onExportMenuToggle: () => void;
  onExport: (format: CriticalResultsExportFormat) => void;
  exportOptions: ExportMenuOption[];
}>) {
  return (
    <div
      className="crd-export-wrap"
      ref={exportWrapRef}
    >
      <button
        type="button"
        className="crd-btn crd-btn--secondary"
        onClick={onExportMenuToggle}
        disabled={exporting || loading}
        aria-busy={exporting}
        aria-expanded={exportMenuOpen}
        aria-haspopup="menu"
      >
        {exporting ? (
          <Loader2
            className="crd-export-spinner"
            size={16}
            strokeWidth={ICON_STROKE_WIDTH}
          />
        ) : (
          <Download
            size={16}
            strokeWidth={ICON_STROKE_WIDTH}
          />
        )}
        {exporting ? 'Exporting…' : 'Export'}
        {exporting ? null : (
          <ChevronDown
            size={16}
            strokeWidth={ICON_STROKE_WIDTH}
          />
        )}
      </button>
      {exportMenuOpen && !exporting ? (
        <div
          className="crd-export-menu"
          role="menu"
        >
          {exportOptions.map(({ format, label, Icon }) => (
            <button
              key={format}
              type="button"
              role="menuitem"
              className="crd-export-menu-item"
              onClick={() => onExport(format)}
            >
              <Icon
                size={16}
                strokeWidth={ICON_STROKE_WIDTH}
                aria-hidden
              />
              {label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: Readonly<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: CriticalResultsFilterOption[];
}>) {
  const selectId = `crd-select-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className="crd-filter-field">
      <label
        className="crd-filter-label"
        htmlFor={selectId}
      >
        {label}
      </label>
      <div className="crd-filter-control crd-filter-control--select">
        <select
          id={selectId}
          className="crd-filter-select"
          value={value}
          onChange={event => onChange(event.target.value)}
        >
          {options.map(option => (
            <option
              key={option.value || '__all__'}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className="crd-filter-icon crd-filter-icon--select"
          size={16}
          strokeWidth={ICON_STROKE_WIDTH}
          aria-hidden
        />
      </div>
    </div>
  );
}

export default function CriticalResultsFilterBar({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  facilityOptions,
  facilityId,
  onFacilityChange,
  showFacilityFilter,
  department,
  departmentOptions,
  onDepartmentChange,
  criticalityBand,
  onCriticalityBandChange,
  status,
  onStatusChange,
  escalationLevel,
  onEscalationLevelChange,
  referringClinician,
  referringClinicianOptions,
  onReferringClinicianChange,
  autoRefresh,
  onAutoRefreshChange,
  lastRefreshedLabel,
  refreshIntervalSec = 60,
  loading,
  refreshing = false,
  exporting = false,
  exportMenuOpen = false,
  onExportMenuToggle,
  onExport,
  onClear,
  onRefresh,
}: Readonly<CriticalResultsFilterBarProps>) {
  const exportWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onExportMenuToggle) {
      return undefined;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (exportWrapRef.current && !exportWrapRef.current.contains(event.target as Node)) {
        if (exportMenuOpen) {
          onExportMenuToggle();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportMenuOpen, onExportMenuToggle]);

  const criticalityOptions: CriticalResultsFilterOption[] = [
    { value: '', label: 'All bands' },
    { value: 'Major', label: 'Critical-Major' },
    { value: 'Minor', label: 'Critical-Minor' },
  ];

  const statusOptions: CriticalResultsFilterOption[] = [...CRITICAL_RESULTS_ACK_STATUS_OPTIONS];

  const levelOptions: CriticalResultsFilterOption[] = [
    { value: '', label: 'All levels' },
    { value: '1', label: 'Level 1' },
    { value: '2', label: 'Level 2' },
    { value: '3', label: 'Level 3' },
    { value: '4', label: 'Level 4' },
  ];

  const exportOptions: ExportMenuOption[] = [
    { format: 'pdf', label: 'Export as PDF', Icon: FileText },
    { format: 'csv', label: 'Export as CSV', Icon: Table2 },
    { format: 'xlsx', label: 'Export as Excel', Icon: FileSpreadsheet },
  ];

  return (
    <section className="crd-filter-section">
      <div className="crd-filters">
        <FilterDate
          label="From"
          value={startDate}
          onChange={onStartDateChange}
          max={endDate}
        />
        <FilterDate
          label="To"
          value={endDate}
          onChange={onEndDateChange}
          min={startDate}
        />
        {showFacilityFilter ? (
          <FilterSelect
            label="Facility"
            value={facilityId != null ? String(facilityId) : ''}
            onChange={value => onFacilityChange(Number(value))}
            options={facilityOptions}
          />
        ) : null}
        <FilterSelect
          label="Department"
          value={department}
          onChange={onDepartmentChange}
          options={departmentOptions}
        />
        <FilterSelect
          label="Criticality"
          value={criticalityBand}
          onChange={onCriticalityBandChange}
          options={criticalityOptions}
        />
        <FilterSelect
          label="Status"
          value={status}
          onChange={onStatusChange}
          options={statusOptions}
        />
        <FilterSelect
          label="Escalation level"
          value={escalationLevel}
          onChange={onEscalationLevelChange}
          options={levelOptions}
        />
        <FilterSelect
          label="Referring clinician"
          value={referringClinician}
          onChange={onReferringClinicianChange}
          options={referringClinicianOptions}
        />
      </div>

      <div className="crd-filter-actions">
        <button
          type="button"
          className="crd-btn crd-btn--ghost"
          onClick={onClear}
        >
          Clear
        </button>
        <button
          type="button"
          className="crd-btn crd-btn--secondary"
          onClick={onRefresh}
          disabled={loading || refreshing || exporting}
        >
          <RefreshCw
            size={16}
            strokeWidth={ICON_STROKE_WIDTH}
            className={refreshing ? 'crd-export-spinner' : undefined}
          />
          Refresh
        </button>
        {onExport && onExportMenuToggle ? (
          <CriticalResultsExportMenu
            exportWrapRef={exportWrapRef}
            exporting={Boolean(exporting)}
            loading={loading}
            exportMenuOpen={Boolean(exportMenuOpen)}
            onExportMenuToggle={onExportMenuToggle}
            onExport={onExport}
            exportOptions={exportOptions}
          />
        ) : null}
        <label className="crd-auto-refresh">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={event => onAutoRefreshChange(event.target.checked)}
          />
          {formatAutoRefreshLabel(autoRefresh, refreshIntervalSec)}
        </label>
        <span
          className="crd-last-refreshed"
          title={lastRefreshedLabel}
        >
          {lastRefreshedLabel}
        </span>
      </div>
    </section>
  );
}
